import type { Driver } from '../../../../../types'
import { CellContext, createColumnHelper } from '@tanstack/react-table'
import { GenericDropdown } from '../../Form/GenericDropdown'
import { WarningTriangle } from '../../../../../icons/WarningTriangle'
import { DriverDropdownItems } from '../components/DriverDropdownItems'
import { FormatPhoneNo } from '../components/FormatPhoneNo'

type DriversCellMeta = {
  alignmentType?: 'string'
  isMandatory?: boolean
}

export const DriversTableData = ({
  editDriver,
  editAccess,
}: {
  editDriver?: (driver: Driver) => void
  editAccess?: (driver: Driver) => void
}) => {
  const columnHelper = createColumnHelper<Driver>()

  const displayCellValue = <T,>(info: CellContext<Driver, T>) => {
    const value = info.getValue()
    const meta = info.column.columnDef.meta as DriversCellMeta | undefined
    const isMandatory = meta?.isMandatory === true

    if (value === null || value === '') {
      return isMandatory ? (
        <div className="flex gap-2 items-center text-error-default">
          <WarningTriangle />
          Mangler data
        </div>
      ) : (
        '-'
      )
    }

    if (typeof value === 'boolean') {
      return value ? 'Ja' : 'Nei'
    }

    return String(value)
  }

  return [
    columnHelper.accessor('name', {
      id: 'name',
      header: 'Navn',
      enableSorting: true,
      sortingFn: 'alphanumeric',
      cell: (info) => displayCellValue(info),
      meta: {
        alignmentType: 'string',
        isMandatory: true,
        mobileLabel: 'Navn',
      },
    }),
    columnHelper.accessor('jobTitle', {
      id: 'jobTitle',
      header: 'Stilling',
      enableSorting: false,
      cell: (info) => displayCellValue(info),
      meta: {
        alignmentType: 'string',
        isMandatory: false,
        mobileLabel: 'Stilling',
      },
    }),
    columnHelper.accessor('phoneNo', {
      id: 'phoneNo',
      header: 'Telefon',
      enableSorting: false,
      cell: (info) => {
        const value = info.getValue()
        if (value === null || value === '') return displayCellValue(info)

        return FormatPhoneNo(value)
      },
      meta: {
        alignmentType: 'string',
        isMandatory: true,
        mobileLabel: 'Telefon',
      },
    }),
    columnHelper.accessor('email', {
      id: 'email',
      header: 'E-post',
      enableSorting: false,
      cell: (info) => displayCellValue(info),
      meta: {
        alignmentType: 'string',
        isMandatory: true,
        mobileLabel: 'E-post',
      },
    }),
    columnHelper.accessor('hasConfiguratorAccess', {
      id: 'hasConfiguratorAccess',
      header: 'Konfig tilgang',
      enableSorting: false,
      cell: (info) => displayCellValue(info),
      meta: { alignmentType: 'string', isMandatory: false, mobileLabel: 'Konfig tilgang' },
    }),
    columnHelper.accessor('webUserType', {
      id: 'webTilgang',
      header: 'Tilgangstype',
      enableSorting: true,
      meta: {
        alignmentType: 'string',
        isMandatory: false,
        mobileLabel: 'Tilgangstype',
      },
      cell: (info) => {
        if (displayCellValue(info) === 'Administrator') {
          return 'Kundetilgang'
        }
        if (displayCellValue(info) == null || displayCellValue(info) === '') {
          return '-'
        }
        return 'Sjåførtilgang'
      },
    }),
    columnHelper.display({
      id: 'actions',
      header: '',
      cell: (info) => {
        return (
          <GenericDropdown
            items={DriverDropdownItems(info.row.original, {
              editDriver,
              editAccess,
            })}
          />
        )
      },
      meta: {
        alignmentType: 'right',
        isMandatory: false,
        variant: 'actions',
      },
    }),
  ]
}
